import { createContext, createRef, type ReactNode, useEffect, useState } from 'react';

import { css } from '@compiled/react';
import {
	type IntersectionOptions,
	useInView,
	// @ts-ignore
} from 'react-intersection-observer';

import { media } from '@atlaskit/primitives';

import { ScrollerItem } from './scroller-item';

export type ScrollerArray<Type> = [Type, ...Array<Type>];

export type ScrollerProps<T = object> = {
	/**
	 * The items to be displayed in the scroller
	 * @type {ScrollerArray<T>} array of at least one item
	 */
	items: ScrollerArray<T>;
	/**
	 * A function that returns the text section to be displayed for each item
	 * @param item An item from the items array
	 */
	renderText: (item: T) => ReactNode;
	/**
	 * A function that returns the graphic section to be displayed for the current item
	 * @param item The current item
	 * @param activeIndex The index of the current item
	 * @param mobileSpecific Whether the graphic is being rendered for mobile
	 */
	renderGraphic: (item: T, activeIndex: number, mobileSpecific?: boolean) => ReactNode;
	/** The heading to be displayed above the scroller */
	heading: ReactNode;
	/**
	 * The side the text shows on
	 * @default left
	 */
	layout?: 'right' | 'left';
	/**
	 * Graphic shows above or below the text on mobile
	 * @default above
	 */
	mobileGraphicLayout?: 'above' | 'below';
	/**
	 * The background color to be displayed when the scroller is not in view
	 * @default #fff
	 */
	backgroundColor?: string;
	/** The background color to be displayed when the scroller is in view */
	inScrollBackgroundColor?: string;
	/** Custom options for the useInView hook for the scroller */
	scrollerInViewOptions?: IntersectionOptions;
	/**
	 * Include Sticky Nav height of X in calculations
	 * @default 68
	 */
	navHeight?: number;
};

export type ScrollContextType = {
	activeIndex: number | 0;
	setActiveIndex: (newValue: number) => void;
	graphicHeight: number | null;
	graphicTopBottom: { top: number; bottom: number } | null;
};

export const ScrollContext = createContext<ScrollContextType>({
	activeIndex: 0,
	setActiveIndex: () => {
		return;
	},
	graphicHeight: 100,
	graphicTopBottom: { top: 188, bottom: 288 },
});

export const Scroller = <T extends object>({
	items,
	renderText,
	renderGraphic,
	heading,
	layout = 'left',
	mobileGraphicLayout = 'above',
	backgroundColor = '#fff',
	inScrollBackgroundColor,
	scrollerInViewOptions,
	navHeight = 68,
}: ScrollerProps<T>) => {
	const { ref: scrollRef, inView: sectionIsVisible } = useInView({
		threshold: 0.2,
		...scrollerInViewOptions,
	});
	const containerRef = createRef<HTMLDivElement>();
	const graphicRef = createRef<HTMLDivElement>();
	const headingRef = createRef<HTMLDivElement>();

	const [activeIndex, setActiveIndex] = useState(0);
	/**
	 * The calculated top position to use for the graphic
	 */
	const [graphicTop, setGraphicTop] = useState(navHeight + 100);
	/**
	 * The height of the graphic, used to set min height on the text column
	 */
	const [graphicHeight, setGraphicHeight] = useState<number | null>(null);
	/**
	 * The calculated top and bottom positions
	 * to use for determining if an item is aligned with the graphic
	 */
	const [graphicTopBottom, setGraphicTopBottom] = useState<{
		top: number;
		bottom: number;
	} | null>(null);

	useEffect(() => {
		if (!containerRef.current || !graphicRef.current || !headingRef.current || !window) {
			return;
		}

		// Where the magic happens
		const calculateSizes = () => {
			// Vertically center the graphic +navHeight for the sticky nav
			const currentGraphicHeight = graphicRef.current?.clientHeight ?? 100;
			const currentHeaderHeight = headingRef.current?.clientHeight ?? 100;
			const viewHeight = window.innerHeight;
			const minGraphicY = navHeight + currentHeaderHeight;
			const top = (viewHeight - navHeight - currentGraphicHeight) / 2 + navHeight;
			const newTop = Math.max(minGraphicY, top);
			const newBottom = Math.min(viewHeight, newTop + currentGraphicHeight);
			setGraphicTop(Math.max(minGraphicY, top));
			setGraphicHeight(currentGraphicHeight);
			setGraphicTopBottom({
				top: newTop,
				bottom: newBottom,
			});
		};

		const observer = new ResizeObserver(() => {
			// Use requestAnimationFrame to defer DOM updates and prevent ResizeObserver loop limit errors
			// This helps prevent the "ResizeObserver loop limit exceeded" warning by deferring DOM updates
			requestAnimationFrame(() => {
				calculateSizes();
			});
		});

		observer.observe(containerRef.current);
		return () => {
			// Cleanup the observer by unobserving all elements
			observer.disconnect();
		};
	}, [containerRef, graphicRef, headingRef, navHeight]);

	const backgroundStyle = css({
		backgroundColor: backgroundColor,
	});
	const inViewBackgroundStyle = css({
		backgroundColor: inScrollBackgroundColor,
	});
	const dynamicHeadingStyle = css({
		insetBlockStart: navHeight ? navHeight + 'px' : 0,
	});
	const inViewHeaderBackgroundStyle = css({
		backgroundImage: `linear-gradient(to bottom, ${inScrollBackgroundColor} 80%, transparent 100%)`,
	});

	const currentItem = items[activeIndex] ?? items[0];

	return (
		<ScrollContext.Provider
			value={{
				activeIndex,
				setActiveIndex,
				graphicHeight,
				graphicTopBottom,
			}}
		>
			<div ref={scrollRef} css={[backgroundStyle, sectionIsVisible && inViewBackgroundStyle]}>
				<div css={[rowStyles, contentStyle, layout === 'right' && rtlRowStyles]} ref={containerRef}>
					<div
						css={headingContainer}
						style={{
							insetBlockEnd: graphicHeight ? graphicHeight + contentBlockEnd + 'px' : 'auto',
						}}
					>
						<div css={headingColumnContainer}>
							<div
								css={[
									rowStyles,
									dynamicHeadingStyle,
									headingStyle,
									sectionIsVisible && inViewHeaderBackgroundStyle,
								]}
								ref={headingRef}
							>
								{heading}
							</div>
						</div>
					</div>
					<div css={textColumn}>
						{items.map((item, i) => (
							<ScrollerItem key={i} index={i}>
								{mobileGraphicLayout === 'above' && (
									<div css={mobileItemStyle}>{renderGraphic(item, activeIndex, true)}</div>
								)}
								{renderText(item)}
								{mobileGraphicLayout === 'below' && (
									<div css={mobileItemStyle}>{renderGraphic(item, activeIndex, true)}</div>
								)}
							</ScrollerItem>
						))}
					</div>
					<div
						css={graphicColumn}
						ref={graphicRef}
						style={{
							insetBlockStart: graphicTop + 'px',
						}}
					>
						{renderGraphic(currentItem, activeIndex, false)}
					</div>
				</div>
			</div>
		</ScrollContext.Provider>
	);
};

const mediaQuery =
	'@media (max-height: 49.99vw) and (max-width: 1271.99px), (max-height: 635.99px) and (min-width: 1272px), not all and (min-width: 48rem)' as const;

const contentBlockEnd = 120 as const;

const rowStyles = css({
	position: 'relative',
	margin: 'auto',
	maxWidth: '100%',
	display: 'flex',
	flexDirection: 'row',

	// inline padding not important to the vertical scrolling
	paddingInline: 32,
	[media.only.sm]: {
		paddingInline: 60,
	},
	[media.above.md]: {
		paddingInline: 60,
		width: 1272,
	},
});

const contentStyle = css({
	paddingBlockEnd: contentBlockEnd,
	paddingBlockStart: 180,
	alignItems: 'flex-start',
	gap: '60px',

	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	[mediaQuery]: {
		paddingBlockStart: 0,
		flexDirection: 'column',
	},
});

const headingContainer = css({
	position: 'absolute',
	insetBlockStart: 0,
	insetInlineStart: 0,
	insetInlineEnd: 0,
	display: 'flex',
	justifyContent: 'center',

	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	[mediaQuery]: {
		position: 'relative',
	},
});

const headingColumnContainer = css({
	flex: '100%',
});

const headingStyle = css({
	zIndex: 1,
	position: 'sticky',

	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	[mediaQuery]: {
		position: 'relative',
		insetBlockStart: 0,
	},
});

const rtlRowStyles = css({
	flexDirection: 'row-reverse',
});

const textColumn = css({
	flexBasis: '40%',

	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	[mediaQuery]: {
		maxWidth: 685,
		alignSelf: 'center',
	},
});

const mobileItemStyle = css({
	display: 'none',

	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	[mediaQuery]: {
		display: 'block',
	},
});

const graphicColumn = css({
	alignItems: 'flex-end',
	display: 'flex',
	flexDirection: 'column',
	justifyContent: 'center',
	flexBasis: '60%',
	position: 'sticky',

	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	[mediaQuery]: {
		display: 'none',
	},
});
